// Types for user agnostic feature gates

/**
 * Configuration registry for user agnostic feature gates
 * Maps Statsig gate names to their default values
 */
export const FEATURE_GATE_REGISTRY = {
  ssr_telemetry_attentive: true,
  ssr_telemetry_claritas: true,
  ssr_telemetry_criteoanalytics: true,
  ssr_telemetry_fb_pixel: true,
  ssr_telemetry_google_analytics: true,
  ssr_telemetry_google_analytics_4: true,
  ssr_telemetry_google_ads_tag: true,
  ssr_telemetry_google_web_container: true,
  ssr_telemetry_internal_analytics: true,
  ssr_telemetry_impact: true,
  'ssr_telemetry_linked_in-pixel': true,
  ssr_telemetry_pebble_post: true,
  ssr_telemetry_poplar: true,
  ssr_telemetry_pinterest_tag: true,
  ssr_telemetry_retention_tracking: true,
  ssr_telemetry_scp: true,
  ssr_telemetry_tatari: true,
  ssr_telemetry_tik_tok_pixel: true,
  ssr_telemetry_datalayer_pixel: true,
  ssr_telemetry_server_sgtm_pixel: true,
  ssr_telemetry_edge_tag: true,
  ssr_telemetry_stylitics: true,
  ssr_mixpanel_identify_by_customer_id: false,
  ssr_mixpanel_sdk_enabled: true, // true for US/CA, false for UK (set via Statsig per locale)
  ssr_mixpanel_sdk_deferred: false,
  mixpanel_region_fix: false,
  ssr_ia_v2: true,
  ssr_csp_headers: false,
  ssr_i18n_us_rollout_gate: false,
  ssr_i18n_us_rollout_distribution: false,
  ssr_i18n_lang_dropdown_enabled: false,
  ssr_app_push_banner: true,
  ssr_use_ultra_lite: false,
  ssr_collection_feature_flag_fallback_implementation: true,
  ssr_site_data_experiment_enabled: true,
  'ssr-use-ultra-light-product-info-for-qsal': false,
  ssr_partial_review_stars: false,
  ssr_wishlist_use_sdk_http_client: false,
  ssr_reporting_services_use_sdk_http_client: false,
  'seo-title-append-quince-locale': false,
  multi_pool_auth_enabled: false,
  ssr_fingerprinting: false,
  ssr_email_subscription: false,
  pdp_optimistic_option_selection: false, // INP: optimistic swatch/size highlight on PDP + product cards
  // Perf: restrict the generic Yotpo widget loader (useYotpoModule in DynamicClientRootLayout) to
  // referral landings — URLs carrying `sref` / `sref_id`. Off (the default, and the value when the
  // Statsig gate is missing or Statsig is unreachable) keeps the legacy behavior: the loader script
  // and its 100ms polling interval run on every page. The dedicated /refer-and-earn and
  // /account/my-rewards loaders are independent of this gate.
  ssr_yotpo_referral_only_loader: false,
  // Perf: load the Forter device-profiling snippet with next/script `lazyOnload` (after window.load
  // + browser idle) instead of `afterInteractive` (immediately after hydration). Off (the default)
  // keeps `afterInteractive`. Forter token consumption is fail-open either way, so the tradeoff is
  // device-token coverage on very fast checkout starts — watch that before ramping to 100%.
  ssr_forter_lazy_onload: false,
  enable_forter_fraud_check: false, // Forter fraud device-profiling kill-switch (off until the Statsig gate is created)
  ssr_b2b_header_footer_override_enabled: true, // Statsig-gated: switches config-listed D2C routes (e.g. /corporate-gifting) to the B2B self-serve style header/footer AND enables the footer new-tab route allowlist. Default fallback true (for testing / when the Statsig gate `ssr_b2b_header_footer_override_enabled` is missing or Statsig is unreachable); once the gate exists it owns the value (evaluated against STATSIG_ANONYMOUS_USER_ID; set 0/100% or rule-based). Contentful `openInNewTab` is honored independently of this gate.
  // Authorizes the `?is_ds=true` DS preview override ONLY — it is NOT the DS decision. The
  // two-level rollout (ds_rollout_page_config__web + RAMP_ds-rollout__web) owns whether DS
  // renders (see 687bfe744 for why a console gate must never drive the decision itself).
  // Fail-closed: gate off (or Statsig unreachable) → ?is_ds is ignored in every environment.
  // Console config: like every gate in this registry it is evaluated against the constant
  // STATSIG_ANONYMOUS_USER_ID, so set it to 0% or 100% (or rule-based targeting) — a partial
  // percentage rollout resolves uniformly for ALL traffic, not per user.
  'ssr-use-design-system-components': false,
} as const

export type UserAgnosticGlobalFeatureGatesFromStatsig = Record<keyof typeof FEATURE_GATE_REGISTRY, boolean>
export interface UserAgnosticGlobalFeatureGatesFromServerSideProps {
  isI18nPreviewEnabled: boolean
}
export type UserAgnosticGlobalFeatureGates = UserAgnosticGlobalFeatureGatesFromStatsig &
  UserAgnosticGlobalFeatureGatesFromServerSideProps

export const userAgnosticGlobalFeatureGatesDefaultsB2B: UserAgnosticGlobalFeatureGates = {
  ...FEATURE_GATE_REGISTRY,
  ssr_ia_v2: true,
  ssr_csp_headers: false,
  ssr_i18n_us_rollout_gate: false,
  ssr_i18n_us_rollout_distribution: false,
  ssr_i18n_lang_dropdown_enabled: false,
  ssr_app_push_banner: false,

  isI18nPreviewEnabled: false,
}

export const userAgnosticGlobalFeatureGatesDefaults: UserAgnosticGlobalFeatureGates = {
  ...FEATURE_GATE_REGISTRY,
  isI18nPreviewEnabled: true,
}

/**
 * Generate entries array from the registry
 * This is stored outside functions since it doesn't change
 */
export const featureGatesEntries = Object.entries(FEATURE_GATE_REGISTRY) as [
  keyof UserAgnosticGlobalFeatureGatesFromStatsig,
  boolean,
][]
